import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { getErrorMessage, isLimitReached } from '../../lib/apiClient';
import { createWorkspace, tenantKeys } from './tenancyApi';

export default function NewWorkspaceModal({ open, onClose, orgId, onCreated }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [limitError, setLimitError] = useState(null); // 422 LIMIT_REACHED body
  // Synchronous guard against a same-tick double click racing ahead of the
  // (state-driven, one-render-later) disabled attribute on the submit button.
  const submittingRef = useRef(false);

  const mutation = useMutation({
    mutationFn: () => createWorkspace({ orgId, name: name.trim() }),
    onSuccess: (workspace) => {
      submittingRef.current = false;
      queryClient.invalidateQueries({ queryKey: tenantKeys.workspaces(orgId) });
      toast.success('Workspace created');
      setName('');
      setLimitError(null);
      onClose();
      onCreated?.(workspace);
    },
    onError: (err) => {
      submittingRef.current = false;
      if (isLimitReached(err)) {
        setLimitError(err.response.data);
      } else {
        toast.error(getErrorMessage(err, 'Could not create workspace'));
      }
    },
  });

  return (
    <Modal open={open} onClose={onClose} title="New workspace" dismissable={!mutation.isPending}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim() && !submittingRef.current) {
            submittingRef.current = true;
            mutation.mutate();
          }
        }}
      >
        {limitError && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
            <span className="flex-1">{limitError.message}</span>
            <Link
              to="/billing"
              onClick={onClose}
              className="shrink-0 font-medium text-brand-600 hover:underline"
            >
              Upgrade plan →
            </Link>
          </div>
        )}
        <Input
          label="Workspace name"
          placeholder="e.g. Finance"
          value={name}
          autoFocus
          onChange={(e) => setName(e.target.value)}
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" loading={mutation.isPending} disabled={!name.trim()}>
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}
